import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { addApproverToUser, removeApproverFromUser } from '@/dataExchange/services/myOrganization.service';
import { ExtendedUserData } from '@/domain/common';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { hasArrayElements } from '@/utils/helpers/helpers';

interface IUseEditApproversLogicProps {
    userId?: string;
    userApprovers?: ExtendedUserData[];
    isModalOpen: boolean;
    onCloseModal: () => void;
    getUserApproversList: () => void;
}

const useEditApproversLogic = ({
    userId,
    userApprovers,
    isModalOpen,
    onCloseModal,
    getUserApproversList,
}: IUseEditApproversLogicProps) => {
    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { handleSentryAction } = useSentryActions();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);

    const userApproversIds = userApprovers?.map(el => el.credentialId) ?? [];

    useEffect(() => {
        if (isModalOpen) {
            setSelectedApprovers(userApproversIds);
        }
    }, [isModalOpen]);

    const approversToAdd = selectedApprovers.filter(id => !userApproversIds.includes(id));
    const approversToDelete = userApprovers?.filter(user => !selectedApprovers.includes(user.credentialId)) ?? [];
    const hasChanges = approversToAdd.length > 0 || approversToDelete.length > 0;

    const handleApproverChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = event.target;
        setSelectedApprovers(prev => (checked ? [...prev, value] : prev.filter(id => id !== value)));
    };

    const handleSubmit = async () => {
        const shouldAdd = hasArrayElements(approversToAdd);
        const shouldRemove = hasArrayElements(approversToDelete);

        setIsLoading(true);

        try {
            if (shouldAdd && userId) {
                try {
                    await Promise.all(
                        approversToAdd.map(async approverId => {
                            await addApproverToUser({ credentialId: userId, managerId: approverId });
                        })
                    );
                    mixpanelTracking(MIXPANEL_ACTIONS.APPROVER_ADDED_TO_USER);
                } catch (error) {
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_ADD_APPROVER_TO_USER);
                    throw error;
                }
            }

            if (shouldRemove) {
                try {
                    await Promise.all(
                        approversToDelete.map(async approver => {
                            approver?.pairedId && (await removeApproverFromUser(approver.pairedId));
                        })
                    );
                    mixpanelTracking(MIXPANEL_ACTIONS.APPROVER_REMOVED_FROM_USER);
                } catch (error) {
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_REMOVE_APPROVER_FROM_USER);
                    throw error;
                }
            }

            createAlert(ALERT_VARIANT.success, t('changes_saved'));
            onCloseModal();
        } catch (error) {
            createAlert(ALERT_VARIANT.error, t('changes_save_failed'));
        } finally {
            getUserApproversList();
            setIsLoading(false);
        }
    };

    const isApproverSelected = (credentialId: string) => selectedApprovers.includes(credentialId);

    return {
        isLoading,
        hasChanges,
        handleApproverChange,
        handleSubmit,
        isApproverSelected,
    };
};

export default useEditApproversLogic;
